import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Center,
  HStack,
  Image,
  VStack,
  Checkbox,
  Text,
  Box,
  Wrap,
  WrapItem,
  SimpleGrid,
  IconButton,
} from '@chakra-ui/react';
import { FaTimes, FaVideo } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import { useProductContext } from '../context/product_context';

function UpdateProductModal({ id }) {
  const {
    single_product: {
      name = '',
      price = '',
      stock = 0,
      description = '',
      colors = [],
      sizes = [],
      category = '',
      ShopName = '',
      images = [],
      videos = [],
      shipping = false,
      featured = false,
      originalPrice = '',
      isOffer = false,
    },
    single_product_loading,
    fetchProducts,
    fetchSingleProduct,
    updateExistingProductDetails,
    updateProduct,
  } = useProductContext();

  const [imageList, setImageList] = useState(images);
  const [videoList, setVideoList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dimensionsInitialized, setDimensionsInitialized] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [customColor, setCustomColor] = useState('#000000');

  // Predefined furniture colors
  const predefinedColors = [
    { name: 'Noir', hex: '#000000' },
    { name: 'Blanc', hex: '#FFFFFF' },
    { name: 'Gris', hex: '#808080' },
    { name: 'Beige', hex: '#F5F5DC' },
    { name: 'Marron', hex: '#8B4513' },
    { name: 'Chêne', hex: '#C4A484' },
    { name: 'Noyer', hex: '#5C4033' },
    { name: 'Wengé', hex: '#645452' },
    { name: 'Or', hex: '#FFD700' },
    { name: 'Argent', hex: '#C0C0C0' },
    { name: 'Bronze', hex: '#CD7F32' },
    { name: 'Bleu', hex: '#0066CC' },
    { name: 'Vert', hex: '#228B22' },
    { name: 'Rouge', hex: '#CC0000' },
  ];

  const addColor = (colorHex) => {
    if (!selectedColors.includes(colorHex)) {
      const newColors = [...selectedColors, colorHex];
      setSelectedColors(newColors);
      updateExistingProductDetails({
        target: { name: 'colors', value: newColors.join(',') },
      });
    }
  };

  const removeColor = (colorHex) => {
    const newColors = selectedColors.filter((c) => c !== colorHex);
    setSelectedColors(newColors);
    updateExistingProductDetails({
      target: { name: 'colors', value: newColors.join(',') },
    });
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialRef = useRef();
  const toast = useToast();

  // Initialize local state when modal opens and loading completes
  useEffect(() => {
    if (isOpen && !single_product_loading) {
      setImageList(images);
      setVideoList(videos ? videos.map((v) => ({ data: v, name: 'Saved video', url: v.url || '' })) : []);

      if (colors && colors.length > 0) {
        if (Array.isArray(colors)) {
          setSelectedColors(colors);
        } else if (typeof colors === 'string') {
          setSelectedColors(colors.split(',').filter(c => c.trim()));
        }
      } else {
        setSelectedColors([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, single_product_loading]);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImageList((prev) => {
          return [...prev, reader.result];
        });
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png, image/gif',
  });

  const onVideoDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      if (file.size > 30 * 1024 * 1024) {
        toast({
          position: 'top',
          description: 'Video must be under 30MB',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setVideoList((prev) => [...prev, { data: reader.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
  }, [toast]);

  const {
    getRootProps: getVideoRootProps,
    getInputProps: getVideoInputProps,
    isDragActive: isVideoDragActive,
  } = useDropzone({
    onDrop: onVideoDrop,
    accept: 'video/mp4, video/webm, video/quicktime, image/gif',
    multiple: true,
  });


  const removeImage = (index) => {
    setImageList((prev) => {
      prev.splice(index, 1);
      return [...prev];
    });
  };

  const removeVideo = (index) => {
    setVideoList((prev) => {
      prev.splice(index, 1);
      return [...prev];
    });
  };

  const handleSubmit = async () => {
    if (
      !name ||
      !price ||
      !stock ||
      selectedColors.length < 1 ||
      !category
    ) {
      return toast({
        position: 'top',
        description: 'Veuillez remplir tous les champs obligatoires (Nom, Prix, Stock, Catégorie, Couleurs)',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    if (imageList.length < 1) {
      return toast({
        position: 'top',
        description: 'Add atleast one image',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading(true);
    const product = {
      name,
      price,
      stock,
      description: description || '',
      colors: selectedColors,
      category,
      ShopName: "",
      shipping,
      featured,
      images: imageList,
      videos: videoList.map((v) => (v.url ? { public_id: v.data?.public_id, url: v.url } : v.data)),
      originalPrice: originalPrice || null,
      discountPercentage: originalPrice ? Math.round((1 - price / originalPrice) * 100) : 0,
      isOffer: isOffer || false,
    };
    const responseCreate = await updateProduct(id, product);
    setLoading(false);
    if (responseCreate.success) {
      onClose();
      toast({
        position: 'top',
        description: 'Product updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      await fetchProducts();
    } else {
      return toast({
        position: 'top',
        description: responseCreate.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };



  return (
    <>
      <Text
        colorScheme='brown'
        minW='100%'
        onClick={() => {
          // Reset state when opening modal
          setDimensionsInitialized(false);
          setSelectedColors([]);
          setSelectedColors([]);
          fetchSingleProduct(id);
          onOpen();
        }}
      >
        Edit
      </Text>

      <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Modifier le produit</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                ref={initialRef}
                placeholder='Product Name'
                name='name'
                focusBorderColor='brown.500'
                value={name}
                onChange={updateExistingProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Price</FormLabel>
              <Input
                type='number'
                placeholder='Product Price'
                name='price'
                focusBorderColor='brown.500'
                value={price}
                onChange={updateExistingProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Stock</FormLabel>
              <Input
                type='number'
                placeholder='Product Stock'
                name='stock'
                focusBorderColor='brown.500'
                value={stock}
                onChange={updateExistingProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Description (Optionnel)</FormLabel>
              <Box bg="white" color="black" borderRadius="md" mb={12}>
                <ReactQuill
                  theme="snow"
                  value={description || ""}
                  onChange={(val) =>
                    updateExistingProductDetails({ target: { name: "description", value: val } })
                  }
                  style={{ height: "200px" }}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike", "blockquote"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ align: [] }],
                      [{ direction: "rtl" }],
                      ["link", "image"],
                      ["clean"],
                    ],
                  }}
                />
              </Box>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Category</FormLabel>
              <Input
                placeholder='Product Category'
                name='category'
                focusBorderColor='brown.500'
                value={category}
                onChange={updateExistingProductDetails}
              />
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Couleurs</FormLabel>

              {/* Selected Colors Display */}
              {selectedColors.length > 0 && (
                <Wrap mb={3}>
                  {selectedColors.map((color, index) => (
                    <WrapItem key={index}>
                      <Box
                        display='flex'
                        alignItems='center'
                        bg='gray.100'
                        borderRadius='full'
                        px={2}
                        py={1}
                      >
                        <Box
                          w='20px'
                          h='20px'
                          borderRadius='full'
                          bg={color}
                          border='2px solid'
                          borderColor='gray.300'
                          mr={2}
                        />
                        <Text fontSize='sm' mr={2}>
                          {predefinedColors.find((c) => c.hex === color)?.name || color}
                        </Text>
                        <IconButton
                          icon={<FaTimes />}
                          size='xs'
                          variant='ghost'
                          colorScheme='red'
                          onClick={() => removeColor(color)}
                          aria-label='Remove color'
                        />
                      </Box>
                    </WrapItem>
                  ))}
                </Wrap>
              )}

              {/* Predefined Colors Grid */}
              <Text fontSize='sm' fontWeight='medium' mb={2}>
                Couleurs prédéfinies:
              </Text>
              <SimpleGrid columns={7} spacing={2} mb={3}>
                {predefinedColors.map((color) => (
                  <Box
                    key={color.hex}
                    w='35px'
                    h='35px'
                    borderRadius='md'
                    bg={color.hex}
                    border='3px solid'
                    borderColor={selectedColors.includes(color.hex) ? 'brown.500' : 'gray.200'}
                    cursor='pointer'
                    onClick={() => addColor(color.hex)}
                    title={color.name}
                    _hover={{ transform: 'scale(1.1)', borderColor: 'brown.300' }}
                    transition='all 0.2s'
                  />
                ))}
              </SimpleGrid>

              {/* Custom Color Picker */}
              <HStack mt={3}>
                <Input
                  type='color'
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  w='60px'
                  h='40px'
                  p={1}
                  cursor='pointer'
                />
                <Input
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  placeholder='#000000'
                  w='100px'
                  size='sm'
                />
                <Button
                  size='sm'
                  colorScheme='brown'
                  onClick={() => addColor(customColor)}
                >
                  Ajouter
                </Button>
              </HStack>
              <FormHelperText>Sélectionnez les couleurs disponibles ou ajoutez une couleur personnalisée</FormHelperText>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Images</FormLabel>
              <Center
                bg='brown.50'
                minHeight={100}
                my={5}
                borderWidth={3}
                borderColor='brown.200'
                borderStyle='dashed'
                borderRadius='lg'
                {...getRootProps()}
              >
                {isDragActive ? (
                  <p>Drag your files here</p>
                ) : (
                  <p>
                    Drag drop image files here, or click to select files
                    <br />
                    (*.jpeg, *.png and *.gif images accepted)
                  </p>
                )}
              </Center>
              <Input {...getInputProps()} />
            </FormControl>

            <FormControl mt={4}>
              <HStack>
                {imageList.map((image, index) => {
                  return (
                    <VStack key={index} spacing={3}>
                      <Image
                        src={image?.url ? image.url : image}
                        boxSize='70px'
                        objectFit='cover'
                        borderRadius='lg'
                      />
                      <Button
                        size='xs'
                        variant='outline'
                        colorScheme='red'
                        onClick={() => removeImage(index)}
                      >
                        Remove
                      </Button>
                    </VStack>
                  );
                })}
              </HStack>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel>Vidéo / GIF (Optionnel)</FormLabel>
              <Center
                bg='purple.50'
                minHeight={80}
                my={3}
                borderWidth={3}
                borderColor='purple.200'
                borderStyle='dashed'
                borderRadius='lg'
                cursor='pointer'
                {...getVideoRootProps()}
              >
                {isVideoDragActive ? (
                  <p>Drop your video/GIF here</p>
                ) : (
                  <VStack spacing={1}>
                    <FaVideo color='#805AD5' size={20} />
                    <Text fontSize='sm' textAlign='center' px={2}>
                      Drag & drop video or GIF here, or click to select
                      <br />
                      (MP4, WebM, GIF — max 30MB)
                    </Text>
                  </VStack>
                )}
              </Center>
              <Input {...getVideoInputProps()} />
              {videoList.length > 0 && (
                <VStack align='stretch' mt={2} spacing={2}>
                  {videoList.map((video, index) => (
                    <HStack key={index} bg='gray.50' p={2} borderRadius='md' justify='space-between'>
                      <HStack spacing={2}>
                        <FaVideo color='#805AD5' />
                        <Text fontSize='sm' isTruncated maxW='180px'>{video.name || 'Saved video'}</Text>
                      </HStack>
                      <Button
                        size='xs'
                        variant='outline'
                        colorScheme='red'
                        onClick={() => removeVideo(index)}
                      >
                        Remove
                      </Button>
                    </HStack>
                  ))}
                </VStack>
              )}
            </FormControl>

            <FormControl mt={4}>
              <Checkbox
                name='shipping'
                colorScheme='brown'
                isChecked={shipping}
                onChange={updateExistingProductDetails}
              >
                Frais de livraison (7 TND)
              </Checkbox>
              <FormHelperText>
                {shipping
                  ? "Les frais de livraison seront de 7 TND pour ce produit"
                  : "Livraison gratuite pour ce produit"
                }
              </FormHelperText>
            </FormControl>

            <FormControl mt={4}>
              <Checkbox
                name='isOffer'
                colorScheme='orange'
                isChecked={isOffer}
                onChange={updateExistingProductDetails}
              >
                Produit en promotion
              </Checkbox>
              <FormHelperText>Cochez pour afficher ce produit dans la page des offres</FormHelperText>
            </FormControl>

            {isOffer && (
              <FormControl mt={4}>
                <FormLabel>Prix original (avant remise)</FormLabel>
                <Input
                  type='number'
                  placeholder='Ex: 150'
                  name='originalPrice'
                  focusBorderColor='orange.500'
                  value={originalPrice}
                  onChange={updateExistingProductDetails}
                />
                <FormHelperText>
                  {originalPrice && price ? (
                    <Text color='green.500' fontWeight='bold'>
                      Remise: -{Math.round((1 - price / originalPrice) * 100)}%
                    </Text>
                  ) : (
                    "Entrez le prix original pour calculer la remise"
                  )}
                </FormHelperText>
              </FormControl>
            )}

            <FormControl mt={4}>
              <Checkbox
                name='featured'
                colorScheme='brown'
                isChecked={featured}
                onChange={updateExistingProductDetails}
              >
                Featured
              </Checkbox>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              isLoading={loading}
              loadingText='Updating Product'
              colorScheme='brown'
              onClick={handleSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >
    </>
  );
}

export default UpdateProductModal;
